import type React from 'react';
import type { InputContainerPropsSingle, TypeaheadMenuProps, TypeaheadResult } from 'react-bootstrap-typeahead';

import './search-by-fail-code.css';

import { ADD_FAIL_CODE_OPTIONS, ANALYTICS_EVENTS, REPORT_TYPES } from '@packages/core/analytics';
import { Button, Icon, Label, Typography } from '@packages/ui/shared';
import Select from '@web/components/select';
import { analyticsService } from '@web/services/singletons';
import { groupBy, throttle } from 'lodash';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, FormControl, InputGroup, Modal, Row } from 'react-bootstrap';
import { AsyncTypeahead, Highlighter, Menu, MenuItem } from 'react-bootstrap-typeahead';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import type { FailCodeSelection, FailCodeVehicleParams } from './fail-codes-modal.types';

import { type FailCodeSearchOption, useSearchByFailCode } from './hooks/use-search-by-fail-code';

/**
 * Props for the {@link SearchByFailCode} component.
 */
export type SearchByFailCodeProps = {
    /** Callback invoked when the user clicks the back button to return to the search method selector. */
    onBack: () => void;

    /** Callback invoked when the user closes the modal. */
    onClose: () => void;

    /** Callback invoked when the user confirms a complete fail code selection. */
    onConfirmFailCode: (selection: FailCodeSelection) => void;

    /** Vehicle params used to scope fail code lookups, already mapped from VIN data by the parent. */
    vehicleParams: FailCodeVehicleParams;
};

/**
 * Search-by-fail-code view within the fail codes modal. Users type a search query to find
 * fail codes, select one from the results, then choose a section and subsection (which may
 * be auto-selected if only one option exists). Renders a typeahead for searching and select
 * dropdowns for section/subsection disambiguation.
 *
 * @param props - {@link SearchByFailCodeProps}
 * @returns the search by fail code JSX
 */
const useSearchByFailCodeStyles = createUseStyles({
    buttonRow: {
        display: 'flex',
        gap: 15,
    },
});

/** Props used to render the fail-code typeahead input. */
type FailCodeSearchInputProps = InputContainerPropsSingle<FailCodeSearchOption> & {
    /** Clears the current fail-code selection. */
    onClear: () => void;
    /** Ref used by the typeahead for menu positioning. */
    referenceElementRef?: (node: HTMLInputElement | null) => void;
    /** Currently selected fail-code option, if any. */
    selectedFailCode: FailCodeSearchOption | null;
};

const disabledSelectOverlayStyle: React.CSSProperties = {
    position: 'absolute',
    width: '95%',
    height: '40px',
    zIndex: 1,
};

/**
 * SearchByFailCode component renders the fail code search flow by direct fail code lookup.
 * Allows the user to search, select, and confirm a fail code.
 *
 * @param props - SearchByFailCodeProps
 * @returns JSX.Element
 */
export function SearchByFailCode({ onBack, onClose, onConfirmFailCode, vehicleParams }: SearchByFailCodeProps) {
    // Hooks
    const classes = useSearchByFailCodeStyles();
    const { t } = useTranslation();

    // Self-contained read-only exception: this hook qualifies because (1) it only performs
    // read-only fail code lookups, (2) the parent does not participate in any intermediate
    // cascade state, and (3) the parent receives the final result via onConfirmFailCode.
    const {
        confirmSelection,
        handleFailCodeSelect,
        handleSearch,
        handleSectionChange,
        handleSubsectionChange,
        isConfirmDisabled,
        isResolving,
        isSearching,
        searchInputRef,
        searchOptions,
        sectionOptions,
        selectedFailCode,
        selectedSection,
        selectedSubsection,
        subsectionOptions,
    } = useSearchByFailCode(vehicleParams);

    const throttledLogSearchNotFound = useMemo(
        () =>
            throttle(
                (searchText: string) => {
                    analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_FAIL_CODE_SEARCH_NOT_FOUND, {
                        search_text: searchText,
                    });
                },
                5000,
                { leading: true, trailing: false }
            ),
        []
    );

    useEffect(() => () => throttledLogSearchNotFound.cancel(), [throttledLogSearchNotFound]);

    const [timesClicked, setTimesClicked] = useState(0);
    const [disabledSectionClicked, setDisabledSectionClicked] = useState(0);
    const [disabledSubsectionClicked, setDisabledSubsectionClicked] = useState(0);

    const disableSubsection =
        !selectedSection ||
        subsectionOptions.length === 0 ||
        (subsectionOptions.length === 1 && selectedSubsection !== null);

    // Handlers

    /**
     * Handle the user selecting a fail code from the typeahead dropdown. Extracts the first
     * selected item and forwards it to the hook's handler.
     *
     * @param selected - array of selected options from the typeahead (always 0 or 1 items)
     */
    const handleTypeaheadChange = useCallback(
        (selected: FailCodeSearchOption[]) => {
            handleFailCodeSelect(selected[0] ?? null);
        },
        [handleFailCodeSelect]
    );

    /**
     * Handle the user typing into the search input. Hides the typeahead menu while the user
     * is still typing to avoid showing stale results.
     */
    const handleSearchInputChange = useCallback(() => {
        searchInputRef.current?.hideMenu();
    }, [searchInputRef]);

    /**
     * Handle the user changing the section dropdown value.
     *
     * @param event - the change event from the select element
     */
    const handleSectionSelectChange = useCallback(
        (event: React.FormEvent<HTMLSelectElement>) => {
            const value = (event.target as HTMLSelectElement).value;
            handleSectionChange(value);
        },
        [handleSectionChange]
    );

    /**
     * Handle the user changing the subsection dropdown value.
     *
     * @param event - the change event from the select element
     */
    const handleSubsectionSelectChange = useCallback(
        (event: React.FormEvent<HTMLSelectElement>) => {
            handleSubsectionChange((event.target as HTMLSelectElement).value);
        },
        [handleSubsectionChange]
    );
    /**
     * Handle the user clicking a disabled section. Increments the click counter and logs an analytics event. The select is wrapped in a div to capture the click event.
     */
    const handleDisabledSectionClick = useCallback(() => {
        setDisabledSectionClicked((prev) => prev + 1);
        analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_FAIL_CODE_DISABLED_BUTTON_CLICKED, {
            report_type: REPORT_TYPES.TECHLINE_CASE,
            times_clicked: disabledSectionClicked + 1,
            field: 'section',
        });
    }, [disabledSectionClicked]);
    /**
     * Handle the user clicking a disabled subsection. Increments the click counter and logs an analytics event. The select is wrapped in a div to capture the click event.
     */

    const handleDisabledSubsectionClick = useCallback(() => {
        setDisabledSubsectionClicked((prev) => prev + 1);
        analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_FAIL_CODE_DISABLED_BUTTON_CLICKED, {
            report_type: REPORT_TYPES.TECHLINE_CASE,
            times_clicked: disabledSubsectionClicked + 1,
            field: 'subsection',
        });
    }, [disabledSubsectionClicked]);
    /**
     * Handle the user clicking the confirm button. Builds the final selection from the
     * hook state and passes it to the parent callback, then closes the modal.
     */
    const handleConfirmButtonClick = useCallback(() => {
        const selection = confirmSelection();
        if (!selection) {
            return;
        }
        analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_FAIL_CODE_SEARCH_ADDED, {
            number_of_fail_codes: 1,
        });
        onConfirmFailCode(selection);
        onClose();
    }, [confirmSelection, onClose, onConfirmFailCode]);

    /**
     * Handle the user clicking the back button.
     */

    const handleBackButtonClick = useCallback(() => {
        analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_FAIL_CODE_BACK_BUTTON_CLICKED, {
            report_type: REPORT_TYPES.TECHLINE_CASE,
            option: ADD_FAIL_CODE_OPTIONS.SEARCH_OR_ENTER_FAIL_CODE,
        });
        onBack();
    }, [onBack]);
    // Derived state

    /** Section options with a placeholder prepended for the select dropdown. */
    const sectionOptionsWithPlaceholder = useMemo(
        () => [
            { title: t('modals:failCodeSearch.failCodeSectionPlaceholder', 'Select section'), value: '' },
            ...sectionOptions,
        ],
        [sectionOptions, t]
    );

    /** Subsection options with a placeholder prepended for the select dropdown. */
    const subsectionOptionsWithPlaceholder = useMemo(
        () => [
            {
                title: t('modals:failCodeSearch.failCodeSubSectionPlaceholder', 'Select subsection'),
                value: '',
            },
            ...subsectionOptions,
        ],
        [subsectionOptions, t]
    );
    /**
     * Logging the LTG link click event for analytics purposes.
     */
    const handleLtgLinkClick = useCallback(() => {
        setTimesClicked((prev) => prev + 1);
        analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_LABOR_TIME_GUIDE_LINK_CLICKED, {
            report_type: REPORT_TYPES.TECHLINE_CASE,
            times_clicked: timesClicked + 1,
        });
    }, [timesClicked]);
    /** The currently selected items for the typeahead's `selected` prop. */
    const typeaheadSelected = useMemo(() => (selectedFailCode ? [selectedFailCode] : []), [selectedFailCode]);

    /**
     * Render the custom dropdown menu for the fail code typeahead. Groups results by category,
     * shows a "no matches" state with a manual entry button when no results exist, and renders
     * highlighted result items within section headers.
     *
     * Exception: this is defined as a local function rather than extracted to a separate component
     * because the react-bootstrap-typeahead `renderMenu` prop API requires a render callback with
     * a fixed signature `(results, menuProps, state)`, and the callback needs access to `t` from
     * the component scope.
     *
     * @param results - the typeahead result items to render
     * @param menuProps - props to spread onto the Menu component
     * @param state - the typeahead state containing the current search text
     * @returns the rendered menu JSX
     */
    const failCodeMenuRenderer = useCallback(
        (
            results: Array<TypeaheadResult<FailCodeSearchOption>>,
            menuProps: TypeaheadMenuProps<FailCodeSearchOption>,
            state: { /** The current search input text. */ text: string }
        ): React.ReactElement => {
            let index = 0;

            // Group results by category, filtering out the typeahead library's pagination objects
            const sections = groupBy(results, (item) => {
                if (item.failCode) {
                    return t('modals:failCodeSearch.resultSections.failCodes', 'Fail Code');
                }
                return 'unknown';
            });

            const items = Object.keys(sections)
                .filter((name) => name !== 'unknown')
                .sort()
                .map((sectionName) => {
                    return (
                        <Fragment key={sectionName}>
                            {index !== 0 && <Menu.Divider />}

                            <Menu.Header>
                                <Typography variant="label">{sectionName}</Typography>
                            </Menu.Header>

                            {sections[sectionName].map((item) => {
                                const menuItem = (
                                    <MenuItem key={index} option={item} position={index}>
                                        <Highlighter search={state.text}>{item.label}</Highlighter>
                                    </MenuItem>
                                );
                                index += 1;
                                return menuItem;
                            })}
                        </Fragment>
                    );
                });

            // Show "no matches" when no results were found
            if (!items.length) {
                throttledLogSearchNotFound(state.text);
                return (
                    <Menu {...menuProps}>
                        <div className="px-4 py-3">
                            <Typography color="grayFive" variant="lead">
                                {t('modals:failCodeSearch.noMatchesFound', 'No matches found.')}
                            </Typography>
                        </div>
                    </Menu>
                );
            }

            return <Menu {...menuProps}>{items}</Menu>;
        },
        [t, throttledLogSearchNotFound]
    );

    const handleClearSection = useCallback(() => {
        analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_FAIL_CODE_SEARCH_CLEARED);
        searchInputRef.current.clear();
        handleFailCodeSelect(null);
    }, [handleFailCodeSelect, searchInputRef]);

    /**
     * Adapts the typeahead's props to the module-level input renderer.
     *
     * @param inputProps - Props supplied by the typeahead
     * @returns The customized typeahead input
     */
    function getTypeaheadInput(inputProps: InputContainerPropsSingle<FailCodeSearchOption>) {
        return renderFailCodeSearchInput({
            ...inputProps,
            onClear: handleClearSection,
            selectedFailCode,
        });
    }
    const typeaheadInputRenderer = useCallback(getTypeaheadInput, [handleClearSection, selectedFailCode]);
    return (
        <>
            <Modal.Header closeButton>
                <Modal.Title>{t('modals:failCodeSearch.title', 'Fail Code Search')}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form as="div" className="failcode-search-form">
                    <Row>
                        <Col>
                            <AsyncTypeahead
                                filterBy={filterByPassthrough}
                                id="fail-code-search"
                                isLoading={isSearching}
                                labelKey={getFailCodeLabel}
                                minLength={2}
                                onChange={handleTypeaheadChange}
                                onInputChange={handleSearchInputChange}
                                onSearch={handleSearch}
                                options={searchOptions}
                                placeholder={t(
                                    'modals:failCodeSearch.searchPlacehodler',
                                    'Search by code or description (min. 2 characters)'
                                )}
                                ref={searchInputRef}
                                renderInput={typeaheadInputRenderer}
                                renderMenu={failCodeMenuRenderer}
                                selected={typeaheadSelected}
                                useCache={false}
                            ></AsyncTypeahead>
                        </Col>
                    </Row>
                    <Row className="my-4">
                        <Typography>
                            Not sure which code you need?{' '}
                            <a
                                href="https://www.subarunet.com/ltg/#/home"
                                onClick={handleLtgLinkClick}
                                rel="noreferrer"
                                target="_blank"
                            >
                                Check the Labor Time Guide
                            </a>
                        </Typography>
                    </Row>
                    <Row>
                        <Col>
                            <div className="mb-4">
                                <Label>{t('modals:failCodeSearch.sectionLabel', 'Section')}</Label>
                                {sectionOptions.length <= 1 && (
                                    <div onClick={handleDisabledSectionClick} style={disabledSelectOverlayStyle} />
                                )}
                                <Select
                                    disabled={sectionOptions.length <= 1}
                                    onChange={handleSectionSelectChange}
                                    options={sectionOptionsWithPlaceholder}
                                    value={JSON.stringify(selectedSection)}
                                />
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Label>{t('modals:failCodeSearch.subsectionLabel', 'Select subsection')}</Label>
                            {disableSubsection && (
                                <div onClick={handleDisabledSubsectionClick} style={disabledSelectOverlayStyle} />
                            )}
                            <Select
                                disabled={disableSubsection}
                                onChange={handleSubsectionSelectChange}
                                options={subsectionOptionsWithPlaceholder}
                                value={JSON.stringify(selectedSubsection)}
                            />
                        </Col>
                    </Row>
                    <hr className="my-4" />
                </Form>
            </Modal.Body>

            <Modal.Footer className="justify-content-between">
                <Button
                    iconLeft={<Icon color="blueOne" name="chevron-left" />}
                    onPress={handleBackButtonClick}
                    variant="ghost-blue"
                >
                    {t('modals:failCodeSearch.actions.back', 'Back')}
                </Button>

                <div className={classes.buttonRow}>
                    <Button onPress={onClose} variant="link-blue">
                        <Typography color="blueOne">{t('qmr:sections:failCode.cancel')}</Typography>
                    </Button>
                    <Button disabled={isConfirmDisabled} isLoading={isResolving} onPress={handleConfirmButtonClick}>
                        {t('modals:failCodeSearch.actions.add', 'Add Fail Code')}
                    </Button>
                </div>
            </Modal.Footer>
        </>
    );
}

/**
 * Passthrough filter function for the AsyncTypeahead. Returns true for all items because
 * server-side filtering is already applied; client-side filtering would remove valid results.
 *
 * @returns true always
 */
function filterByPassthrough(): boolean {
    return true;
}

/**
 * Extract a display label from a fail code search option for the typeahead.
 *
 * @param option - the fail code search option
 * @returns a formatted string like "A1234 - Engine misfire"
 */
function getFailCodeLabel(option: FailCodeSearchOption): string {
    return option.label;
}

/**
 * Renders the typeahead input with its search or clear icon.
 *
 * @param props - Typeahead input props and fail-code selection state
 * @returns The customized typeahead input
 */
function renderFailCodeSearchInput(props: FailCodeSearchInputProps): React.ReactElement {
    /**
     * Forwards the input node to both refs used by the typeahead.
     *
     * @param node - The input DOM node
     */
    function handleInputRef(node: HTMLInputElement | null) {
        if (typeof props.inputRef === 'function') {
            props.inputRef(node);
        }
        props.referenceElementRef?.(node);
    }

    return (
        <InputGroup>
            <FormControl
                aria-activedescendant={props['aria-activedescendant']}
                aria-autocomplete={props['aria-autocomplete']}
                aria-expanded={props['aria-expanded']}
                aria-haspopup={props['aria-haspopup']}
                aria-owns={props['aria-owns']}
                autoComplete={props.autoComplete}
                className={props.inputClassName}
                disabled={props.disabled}
                id={props.id}
                onBlur={props.onBlur}
                onChange={props.onChange as unknown as React.ComponentProps<typeof FormControl>['onChange']}
                onClick={props.onClick}
                onFocus={props.onFocus}
                onKeyDown={props.onKeyDown}
                placeholder={props.placeholder}
                ref={handleInputRef}
                role={props.role}
                type="text"
                value={props.value}
            />
            {props.selectedFailCode ? (
                <button
                    aria-label="Clear fail code"
                    className="failcode-clear-button"
                    onClick={props.onClear}
                    type="button"
                >
                    <Icon name="close" />
                </button>
            ) : (
                <Icon name="search" />
            )}
        </InputGroup>
    );
}
